import React, { useState, useEffect, useMemo } from 'react';
import {
  ArrowLeft,
  Edit2,
  Download,
  Trash2,
  Brain,
  MessageSquare,
  Activity,
  Sparkles,
  Database
} from 'lucide-react';
import { BigDataDataset } from '../../services/BigDataDatasetService';
import DatasetChat from './DatasetChat';
import InsightsTimeline from './InsightsTimeline';
import VirtualDataGrid from './VirtualDataGrid';
import AINavigatorController from './AINavigatorController';
import { DatasetMetadata } from '../../lib/duckdb-engine';
import { debugLog } from '../../utils/debug-logger';

interface DatasetWorkspaceProps {
  dataset: BigDataDataset;
  onBack: () => void;
  onUpdate: () => void;
  onQueryStart?: () => void; // ✅ Callback pour notifier le début d'une analyse
}

const DatasetWorkspace: React.FC<DatasetWorkspaceProps> = ({
  dataset,
  onBack,
  onUpdate,
  onQueryStart
}) => {
  debugLog('[🖥️ DatasetWorkspace] COMPONENT RENDER');
  debugLog('[🖥️ DatasetWorkspace] Dataset received:', {
    id: dataset.id,
    file_name: dataset.file_name,
    job_id: dataset.job_id,
    status: dataset.status,
    row_count: dataset.row_count,
    column_count: dataset.column_count,
    hasColumns: !!dataset.columns,
    columnsType: typeof dataset.columns,
    columnsIsArray: Array.isArray(dataset.columns),
    columnsLength: dataset.columns?.length
  });
  debugLog('[🔍 DatasetWorkspace] RAW dataset.columns:', dataset.columns);
  
  const [activeTab, setActiveTab] = useState<'data' | 'chat' | 'insights'>('data');
  const [isRenaming, setIsRenaming] = useState(false);
  const [newName, setNewName] = useState(dataset.file_name);

  const tabs = [
    { id: 'data', label: 'Tableau', icon: Database, color: 'green' },
    { id: 'chat', label: 'Questions', icon: MessageSquare, color: 'blue' },
    { id: 'insights', label: 'Découvertes', icon: Sparkles, color: 'pink' }
  ];

  // ✅ MEMO: Convertir BigDataDataset en DatasetMetadata pour VirtualDataGrid
  // Ne recalcule QUE si dataset.job_id change (pas à chaque render)
  const datasetMetadata = useMemo(() => {
    debugLog('[🔄 DatasetWorkspace] Converting BigDataDataset to DatasetMetadata...');
    
    const metadata: DatasetMetadata & { isBackend?: boolean; backendJobId?: string; status?: 'processing' | 'completed' | 'error' } = {
      id: dataset.job_id,
      name: dataset.file_name,
      rowCount: dataset.row_count || 0,
      columnCount: dataset.column_count || 0,
      columns: Array.isArray(dataset.columns) ? dataset.columns : [],
      size: dataset.file_size,
      uploadedAt: new Date(dataset.created_at),
      isBackend: true,
      backendJobId: dataset.job_id,
      status: dataset.status as 'processing' | 'completed' | 'error',
    };
    
    debugLog('[✅ DatasetWorkspace] DatasetMetadata created:', {
      id: metadata.id,
      name: metadata.name,
      rowCount: metadata.rowCount,
      columnCount: metadata.columnCount,
      hasColumns: !!metadata.columns,
      columnsLength: metadata.columns?.length,
      status: metadata.status
    });
    debugLog('[🔍 DatasetWorkspace] DatasetMetadata.columns:', metadata.columns);
    
    return metadata;
  }, [dataset.job_id, dataset.file_name, dataset.row_count, dataset.column_count, dataset.columns, dataset.file_size, dataset.created_at, dataset.status]);

  const handleRename = async () => {
    // TODO: Implement rename via service
    setIsRenaming(false);
    onUpdate();
  };

  const handleExport = () => {
    // TODO: Implement export
    alert('Export en cours de développement');
  };

  const handleDelete = async () => {
    if (!confirm(`Supprimer définitivement "${dataset.file_name}" ?`)) return;
    // TODO: Implement delete
    onBack();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50/30 via-purple-50/20 to-blue-50/30 dark:from-slate-950 dark:via-slate-950 dark:to-slate-950">
      {/* Header */}
      <div className="border-b border-purple-200 dark:border-slate-700 bg-gradient-to-r from-purple-50/80 via-blue-50/80 to-indigo-50/80 dark:from-slate-900/80 dark:via-slate-900/80 dark:to-slate-900/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Back + Title */}
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="p-2 hover:bg-purple-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                title="Retour"
              >
                <ArrowLeft className="w-5 h-5 text-purple-600 dark:text-gray-400" />
              </button>
              
              {isRenaming ? (
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleRename()}
                    onBlur={handleRename}
                    autoFocus
                    className="px-3 py-1 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-slate-800 dark:to-slate-800 border border-purple-500 dark:border-purple-500 rounded-lg text-purple-900 dark:text-white focus:outline-none"
                  />
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <Brain className="w-6 h-6 text-purple-600 dark:text-purple-500" />
                  <h1 className="text-xl font-semibold bg-gradient-to-r from-purple-700 to-indigo-700 dark:from-white dark:to-white bg-clip-text text-transparent">
                    {dataset.file_name}
                  </h1>
                  <button
                    onClick={() => setIsRenaming(true)}
                    className="p-1 hover:bg-purple-100 dark:hover:bg-slate-800 rounded transition-colors"
                    title="Renommer"
                  >
                    <Edit2 className="w-4 h-4 text-purple-600 dark:text-gray-400" />
                  </button>
                </div>
              )}
            </div>

            {/* Right: Actions */}
            <div className="flex items-center space-x-2">
              <button
                onClick={handleExport}
                className="px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 dark:from-slate-800 dark:to-slate-800 dark:hover:from-slate-700 dark:hover:to-slate-700 text-blue-900 dark:text-white rounded-lg transition-all shadow-sm inline-flex items-center space-x-2 border border-blue-200 dark:border-slate-700"
                title="Exporter"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline font-medium">Exporter</span>
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-100 hover:to-pink-100 dark:from-slate-800 dark:to-slate-800 dark:hover:from-slate-700 dark:hover:to-slate-700 text-red-700 dark:text-red-400 rounded-lg transition-all shadow-sm inline-flex items-center space-x-2 border border-red-200 dark:border-red-500/30"
                title="Supprimer"
              >
                <Trash2 className="w-4 h-4" />
                <span className="hidden sm:inline">Supprimer</span>
              </button>
            </div>
          </div>

          {/* Stats Row */}
          <div className="mt-4 flex items-center space-x-6 text-sm text-purple-700 dark:text-gray-400">
            <div className="flex items-center space-x-2 bg-purple-50 dark:bg-slate-800 px-3 py-1.5 rounded-lg">
              <Activity className="w-4 h-4" />
              <span>{dataset.row_count?.toLocaleString() || '0'} lignes</span>
            </div>
            <div className="flex items-center space-x-2 bg-blue-50 dark:bg-slate-800 px-3 py-1.5 rounded-lg">
              <span>{dataset.column_count || '0'} colonnes</span>
            </div>
            <div className="flex items-center space-x-2 bg-indigo-50 dark:bg-slate-800 px-3 py-1.5 rounded-lg">
              <span>{(dataset.file_size / (1024 * 1024)).toFixed(1)} MB</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${
                dataset.status === 'completed' ? 'bg-green-500' :
                dataset.status === 'processing' ? 'bg-yellow-500 animate-pulse' :
                'bg-red-500'
              }`} />
              <span className="capitalize">{dataset.status}</span>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-4 flex items-center space-x-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`px-4 py-2 rounded-lg transition-all duration-200 inline-flex items-center space-x-2 shadow-sm ${
                    isActive
                      ? tab.color === 'green'
                        ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:bg-green-500/20 text-green-700 dark:text-green-400 border-2 border-green-400 dark:border-green-500/50 shadow-green-200 dark:shadow-none'
                        : tab.color === 'blue'
                        ? 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 border-2 border-blue-400 dark:border-blue-500/50 shadow-blue-200 dark:shadow-none'
                        : 'bg-gradient-to-r from-pink-50 to-purple-50 dark:bg-pink-500/20 text-pink-700 dark:text-pink-400 border-2 border-pink-400 dark:border-pink-500/50 shadow-pink-200 dark:shadow-none'
                      : 'text-purple-600 dark:text-gray-400 hover:bg-purple-50 dark:hover:bg-slate-800 border border-transparent hover:border-purple-200 dark:hover:border-slate-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content - ✅ TOUS LES TABS MONTÉS, cache avec display */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className={activeTab === 'data' ? 'block' : 'hidden'}>
          <VirtualDataGrid
            dataset={datasetMetadata}
            isBackend={true}
            backendJobId={dataset.job_id}
            pageSize={1000}
          />
        </div>
        
        <div className={activeTab === 'chat' ? 'block' : 'hidden'}>
          <DatasetChat 
            dataset={dataset}
          />
        </div>
        
        <div className={activeTab === 'insights' ? 'block' : 'hidden'}>
          <InsightsTimeline dataset={dataset} />
        </div>
      </div>
    </div>
  );
};

export default DatasetWorkspace;



